import { Link } from "react-router-dom";
import PageLayout from "@/components/aoria/PageLayout";
import PageHeader from "@/components/aoria/PageHeader";
import Seo from "@/components/aoria/Seo";

/**
 * Page « Pour qui » — orientation rapide vers les ressources selon le profil.
 * Cible explicitement les 5 publics : institutions, entreprises, services RH,
 * salariés, indépendants.
 */
const PROFILS = [
    {
        num: "01",
        kind: "Institution publique",
        body: "Collectivités, administrations, établissements d'enseignement, organismes de service public : comprendre l'enjeu de l'accessibilité cognitive dans la conception des services et de l'information.",
        cta: { label: "Lire les enjeux", to: "/enjeu" },
    },
    {
        num: "02",
        kind: "Entreprise",
        body: "Dirigeants et équipes voulant intégrer l'accessibilité cognitive dans la conception des produits, des outils internes, des supports clients ou de la formation.",
        cta: { label: "Voir les outils", to: "/outils" },
    },
    {
        num: "03",
        kind: "Service RH",
        body: "Responsables RH, managers, référents handicap : accompagner les salariés concernés, prévenir l'exclusion par l'outil, structurer une démarche d'inclusion cognitive.",
        cta: { label: "Lire les enjeux", to: "/enjeu" },
    },
    {
        num: "04",
        kind: "Salarié",
        body: "Vous rencontrez des obstacles cognitifs au travail : surcharge, lecture difficile, démarches opaques. Les outils numériques peuvent vous aider, à condition d'en connaître les usages et les limites.",
        cta: { label: "Voir les outils", to: "/outils" },
    },
    {
        num: "05",
        kind: "Indépendant",
        body: "Indépendants, freelances, dirigeants de TPE : gérer seul l'ensemble d'une activité demande un effort cognitif important. Aoria documente les usages utiles et les pièges à éviter.",
        cta: { label: "Voir les outils", to: "/outils" },
    },
];

export default function PourQui() {
    return (
        <PageLayout testid="pour-qui-page">
            <Seo
                title="Pour qui · Aoria s'adresse à toutes les organisations et personnes concernées"
                description="Aoria s'adresse aux institutions publiques, aux entreprises, aux services RH, aux salariés et aux indépendants concernés par l'accessibilité cognitive à l'ère numérique."
                path="/pour-qui"
            />
            <PageHeader
                testid="pour-qui"
                eyebrow="POUR QUI"
                title="Aoria s'adresse à celles et ceux"
                italic="qui font face à l'accessibilité cognitive"
                lead="Institutions publiques, entreprises, services RH, salariés, indépendants : cinq profils, cinq portes d'entrée vers les ressources d'Aoria. Chaque entrée renvoie vers les contenus les plus utiles pour vous."
                backdrop="network"
            />

            <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {PROFILS.map((p, i) => (
                        <article
                            key={p.kind}
                            data-testid={`pour-qui-profil-${i}`}
                            className="card-aoria"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <span className="editorial-num text-sm">
                                    {p.num}
                                </span>
                                <span className="eyebrow text-[#778DA9]">
                                    Profil
                                </span>
                            </div>
                            <h2 className="h3 text-xl md:text-2xl text-[#0D1B2A] leading-snug">
                                {p.kind}
                            </h2>
                            <p className="mt-5 text-[#5C6B7D] text-sm md:text-base leading-relaxed flex-1">
                                {p.body}
                            </p>
                            <Link
                                to={p.cta.to}
                                data-testid={`pour-qui-cta-${i}`}
                                className="mt-7 pt-5 border-t border-[#E0E1DD] text-sm text-[#0D1B2A] link-underline self-start"
                            >
                                {p.cta.label} →
                            </Link>
                        </article>
                    ))}
                </div>

                <div className="mt-20 md:mt-24 max-w-3xl">
                    <p className="text-[#1B263B] text-base md:text-lg leading-relaxed">
                        Vous ne vous reconnaissez pas dans ces profils ? Aoria
                        s'adresse plus largement à toutes les personnes et
                        organisations concernées par l'accessibilité cognitive
                        à l'ère numérique.{" "}
                        <Link
                            to="/contact"
                            className="link-underline text-[#0D1B2A]"
                        >
                            Écrivez-nous
                        </Link>
                        .
                    </p>
                </div>
            </section>
        </PageLayout>
    );
}
